import React, { useEffect, useState } from 'react';
import { Button, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { MASK_CEP, MASK_CNPJ_ALFANUMERICO } from '~/constant';
import {
  atualizarPrestadorPj,
  buscarEnderecoPorCEP,
  criarPrestadorPj,
  listarBancosJuridico,
  listarCidadesJuridico,
  listarUfsJuridico,
  listarUsuariosJuridico,
} from '~/Services/Service';
import { cepSchema, cnpjSchema } from '~/validationSchemas';
import {
  PERCENTUAL_RETENCAO_DEFAULT,
  TIPO_REMUNERACAO_FIXO,
  TIPO_REMUNERACAO_HORA,
} from '../utils/formatters';
import ModalAddSocio from './ModalAddSocio';

const vazio = {
  codigoUsuario: '',
  razaoSocial: '',
  nomeFantasia: '',
  cnpj: '',
  dataAbertura: '',
  banco: '',
  agencia: '',
  conta: '',
  enderecoLogradouro: '',
  enderecoNumero: '',
  enderecoComplemento: '',
  enderecoBairro: '',
  enderecoCidade: '',
  enderecoUf: '',
  enderecoCep: '',
  // DW-0982 — Melhoria Valor Fixo: vigência inicial bifurcada (Hora ↔ Fixo).
  tipoRemuneracao: TIPO_REMUNERACAO_HORA,
  valorHora: '',
  valorFixo: '',
  horasBase: '',
  // DW-0982 — Melhoria 2 Modal PJ: percentuais da vigência ativa, exibidos read-only.
  // Default 10/10/10 em CRIAR; em EDITAR, useEffect lê do `prestador` (vindo do detalhe).
  percentual13: PERCENTUAL_RETENCAO_DEFAULT,
  percentual14: PERCENTUAL_RETENCAO_DEFAULT,
  percentualFundo: PERCENTUAL_RETENCAO_DEFAULT,
};

const limparCnpj = value => (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const somenteNumeros = value => (value || '').replace(/\D/g, '');
const dataInput = valor => (valor ? String(valor).slice(0, 10) : '');

const ModalAddPJ = ({ show, onHide, onSalvo, prestador }) => {
  const [form, setForm] = useState(vazio);
  const [socios, setSocios] = useState([]);
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [erros, setErros] = useState({});
  const [modalSocio, setModalSocio] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const ehEdicao = !!prestador;

  useEffect(() => {
    if (!show) return;
    if (prestador) {
      // PrestadorDetalheResponse traz tipo_remuneracao + valor_hora|valor_fixo+horas_base
      // (DW-0982 — Melhoria Valor Fixo). Default 'H' para PJs antigos sem vigência ativa.
      const tipo = prestador.tipo_remuneracao || TIPO_REMUNERACAO_HORA;
      setForm({
        codigoUsuario: prestador.codigo_usuario || '',
        razaoSocial: prestador.razao_social || '',
        nomeFantasia: prestador.nome_fantasia || '',
        cnpj: prestador.cnpj || '',
        dataAbertura: dataInput(prestador.data_abertura),
        banco: prestador.banco || '',
        agencia: prestador.agencia || '',
        conta: prestador.conta || '',
        enderecoLogradouro: prestador.endereco_logradouro || '',
        enderecoNumero: prestador.endereco_numero || '',
        enderecoComplemento: prestador.endereco_complemento || '',
        enderecoBairro: prestador.endereco_bairro || '',
        enderecoCidade: prestador.endereco_cidade || '',
        enderecoUf: prestador.endereco_uf || '',
        enderecoCep: prestador.endereco_cep || '',
        tipoRemuneracao: tipo,
        valorHora: prestador.valor_hora != null ? Number(prestador.valor_hora) : '',
        valorFixo: prestador.valor_fixo != null ? Number(prestador.valor_fixo) : '',
        horasBase: prestador.horas_base != null ? Number(prestador.horas_base) : '',
        // DW-0982 — Melhoria 2: percentuais da vigência ativa do PJ (read-only).
        percentual13: prestador.percentual_13 != null ? Number(prestador.percentual_13) : PERCENTUAL_RETENCAO_DEFAULT,
        percentual14: prestador.percentual_14 != null ? Number(prestador.percentual_14) : PERCENTUAL_RETENCAO_DEFAULT,
        percentualFundo: prestador.percentual_fundo != null ? Number(prestador.percentual_fundo) : PERCENTUAL_RETENCAO_DEFAULT,
      });
      setSocios((prestador.socios || []).map(socio => ({
        codigo: socio.codigo,
        nome: socio.nome,
        cpf: socio.cpf,
        percentualParticipacao: socio.percentual_participacao,
      })));
    } else {
      setForm(vazio);
      setSocios([]);
    }
    setErros({});
    setBuscandoCep(false);
    listarUfsJuridico().then(setUfs).catch(() => setUfs([]));
    listarCidadesJuridico().then(setCidades).catch(() => setCidades([]));
    listarUsuariosJuridico().then(setUsuarios).catch(() => setUsuarios([]));
    listarBancosJuridico().then(setBancos).catch(() => setBancos([]));
  }, [show, prestador]);

  const alterar = event => {
    const { name, value } = event.target;
    let valor = name === 'cnpj' ? value.toUpperCase() : value;
    if (name === 'agencia') valor = somenteNumeros(value).slice(0, 5);
    if (name === 'conta') valor = somenteNumeros(value).slice(0, 20);
    if (name === 'enderecoNumero') valor = somenteNumeros(value).slice(0, 4);
    setForm(atual => ({ ...atual, [name]: valor }));
    setErros(atuais => ({ ...atuais, [name]: null }));
  };

  const removerSocio = cpf => {
    setSocios(atuais => atuais.filter(s => s.cpf !== cpf));
  };

  const adicionarSocio = socio => {
    setSocios(atuais => [...atuais.filter(s => s.cpf !== socio.cpf), socio]);
  };

  const usuarioOptions = usuarios.map(usuario => ({
    value: usuario.codigo_usuario,
    label: `${usuario.codigo_usuario} - ${usuario.nome_completo}`,
  }));

  const usuarioSelecionado = usuarioOptions.find(
    option => option.value === Number(form.codigoUsuario)
  ) || null;

  const bancoOptions = bancos.map(banco => ({
    value: banco.descricao,
    label: `${banco.codigo} - ${banco.descricao}`,
    searchText: `${banco.codigo} ${banco.descricao}`,
  }));

  const bancoSelecionado = bancoOptions.find(
    option => option.value === form.banco
  ) || (form.banco ? { value: form.banco, label: form.banco } : null);

  const alterarUsuario = option => {
    setForm(atual => ({
      ...atual,
      codigoUsuario: option ? option.value : '',
    }));
    setErros(atuais => ({ ...atuais, codigoUsuario: null }));
  };

  const alterarBanco = option => {
    setForm(atual => ({
      ...atual,
      banco: option ? option.value : '',
    }));
    setErros(atuais => ({ ...atuais, banco: null }));
  };

  // Cidade filtra por UF selecionada. Persiste o nome (Endereco_Cidade é VARCHAR).
  const cidadeOptions = form.enderecoUf
    ? cidades
        .filter(cidade => cidade.estado === form.enderecoUf)
        .map(cidade => ({ value: cidade.descricao, label: cidade.descricao, estado: cidade.estado }))
    : [];

  const cidadeSelecionada =
    cidadeOptions.find(option => option.value === form.enderecoCidade) ||
    (form.enderecoCidade ? { value: form.enderecoCidade, label: form.enderecoCidade } : null);

  const alterarUf = event => {
    const { value } = event.target;
    // Trocar a UF muda o filtro do select de Cidade — limpa a cidade anterior.
    setForm(atual => ({ ...atual, enderecoUf: value, enderecoCidade: '' }));
    setErros(atuais => ({ ...atuais, enderecoUf: null, enderecoCidade: null }));
  };

  const alterarCidade = option => {
    setForm(atual => ({
      ...atual,
      enderecoCidade: option ? option.value : '',
      enderecoUf: option && option.estado ? option.estado : atual.enderecoUf,
    }));
    setErros(atuais => ({ ...atuais, enderecoCidade: null }));
  };

  // Autopreenchimento por CEP (padrão do ModalFornecedor): popula Logradouro, Bairro,
  // Cidade e UF a partir de /busca/cep (resposta traz cidade já como nome + uf).
  const buscarEnderecoCep = async () => {
    const cep = (form.enderecoCep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const res = await buscarEnderecoPorCEP(cep);
      if (!res) return;
      setForm(atual => ({
        ...atual,
        enderecoLogradouro: res.logradouro ? res.logradouro : atual.enderecoLogradouro,
        enderecoBairro: res.bairro ? res.bairro : atual.enderecoBairro,
        enderecoCidade: res.cidade ? res.cidade : atual.enderecoCidade,
        enderecoUf: res.uf ? res.uf : atual.enderecoUf,
      }));
      setErros(atuais => ({
        ...atuais,
        enderecoLogradouro: null,
        enderecoBairro: null,
        enderecoCidade: null,
        enderecoUf: null,
      }));
    } catch (err) {
      toast.error('CEP não encontrado.');
    } finally {
      setBuscandoCep(false);
    }
  };

  const validarObrigatorios = () => {
    const novosErros = {};
    if (!form.codigoUsuario || Number(form.codigoUsuario) <= 0) {
      novosErros.codigoUsuario = 'Usuário deve ser preenchido.';
    }
    if (!form.razaoSocial || !form.razaoSocial.trim()) {
      novosErros.razaoSocial = 'Razão social deve ser preenchida.';
    }
    if (!form.cnpj || !limparCnpj(form.cnpj)) {
      novosErros.cnpj = 'CNPJ deve ser preenchido.';
    }
    if (!form.dataAbertura) {
      novosErros.dataAbertura = 'Data abertura deve ser preenchida.';
    }
    // DW-0982 P1: a remuneração não é coletada no cadastro do PJ — sem validação aqui.
    // Dados bancários opcionais (DW-0982 melhoria #1): validar formato apenas quando preenchidos.
    if (form.agencia && form.agencia.trim() && !/^\d{1,5}$/.test(form.agencia)) {
      novosErros.agencia = 'Agência deve conter até 5 caracteres numéricos.';
    }
    if (form.conta && form.conta.trim() && !/^\d{1,20}$/.test(form.conta)) {
      novosErros.conta = 'Conta deve conter até 20 caracteres numéricos.';
    }
    if (!form.enderecoLogradouro || !form.enderecoLogradouro.trim()) {
      novosErros.enderecoLogradouro = 'Logradouro deve ser preenchido.';
    } else if (form.enderecoLogradouro.trim().length > 100) {
      novosErros.enderecoLogradouro = 'Logradouro deve conter até 100 caracteres.';
    }
    if (!form.enderecoNumero || Number(form.enderecoNumero) <= 0) {
      novosErros.enderecoNumero = 'Número deve ser preenchido.';
    } else if (!/^\d{1,4}$/.test(form.enderecoNumero)) {
      novosErros.enderecoNumero = 'Número deve conter até 4 caracteres numéricos.';
    }
    if (!form.enderecoBairro || !form.enderecoBairro.trim()) {
      novosErros.enderecoBairro = 'Bairro deve ser preenchido.';
    } else if (form.enderecoBairro.trim().length > 60) {
      novosErros.enderecoBairro = 'Bairro deve conter até 60 caracteres.';
    }
    if (form.enderecoComplemento && form.enderecoComplemento.trim().length > 60) {
      novosErros.enderecoComplemento = 'Complemento deve conter até 60 caracteres.';
    }
    if (!form.enderecoCidade || !form.enderecoCidade.trim()) {
      novosErros.enderecoCidade = 'Cidade deve ser preenchida.';
    }
    if (!form.enderecoUf || !form.enderecoUf.trim()) {
      novosErros.enderecoUf = 'UF deve ser preenchida.';
    }
    if (!form.enderecoCep || !form.enderecoCep.replace(/\D/g, '')) {
      novosErros.enderecoCep = 'CEP deve ser preenchido.';
    }
    try {
      cnpjSchema.validateSync(form.cnpj);
    } catch (err) {
      novosErros.cnpj = err.message;
    }
    try {
      cepSchema.validateSync(form.enderecoCep);
    } catch (err) {
      novosErros.enderecoCep = err.message;
    }
    return novosErros;
  };

  const validar = async () => {
    const novosErros = validarObrigatorios();
    if (Object.keys(novosErros).length) {
      setErros(novosErros);
      throw new Error('Preencha os campos obrigatórios.');
    }

    if (!ehEdicao) {
      const soma = socios.reduce((total, socio) => total + Number(socio.percentualParticipacao || 0), 0);
      if (Math.round(soma * 100) / 100 !== 100) {
        throw new Error('A soma dos percentuais dos sócios deve ser 100%.');
      }
    }
    if (!form.codigoUsuario || Number(form.codigoUsuario) <= 0) {
      throw new Error('Informe o usuário GES do prestador.');
    }
  };

  const salvar = async () => {
    try {
      await validar();
      setSalvando(true);
      const payload = {
        codigo_usuario: Number(form.codigoUsuario),
        razao_social: form.razaoSocial,
        nome_fantasia: form.nomeFantasia,
        cnpj: limparCnpj(form.cnpj),
        data_abertura: form.dataAbertura,
        banco: form.banco,
        agencia: form.agencia,
        conta: form.conta,
        endereco_logradouro: form.enderecoLogradouro,
        endereco_numero: Number(form.enderecoNumero || 0),
        endereco_complemento: form.enderecoComplemento,
        endereco_bairro: form.enderecoBairro,
        endereco_cidade: form.enderecoCidade,
        endereco_uf: form.enderecoUf,
        endereco_cep: form.enderecoCep.replace(/\D/g, ''),
        // DW-0982 P1: o cadastro do PJ NÃO envia remuneração — ela vem do contrato.
      };

      if (ehEdicao) {
        await atualizarPrestadorPj({
          ...payload,
          codigo: prestador.codigo,
        });
      } else {
        await criarPrestadorPj({
          ...payload,
          socios: socios.map(socio => ({
            codigo: socio.codigo,
            nome: socio.nome,
            cpf: socio.cpf,
            percentual_participacao: Number(socio.percentualParticipacao),
          })),
        });
      }
      toast.success(ehEdicao ? 'Prestador atualizado com sucesso.' : 'Prestador cadastrado com sucesso.');
      setForm(vazio);
      setSocios([]);
      onSalvo();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar prestador.');
    } finally {
      setSalvando(false);
    }
  };

  const RequiredLabel = ({ children }) => (
    <Form.Label>
      {children}
      <span className="hpj-required">*</span>
    </Form.Label>
  );

  const FieldError = ({ name }) => (
    erros[name] ? <div className="hpj-field-error">{erros[name]}</div> : null
  );

  return (
    <Modal
      className={`hpj-modal hpj-modal-pj${modalSocio ? ' hpj-modal-stacked-back' : ''}`}
      show={show}
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <div>
          <div className="hpj-modal-eyebrow">Cadastro</div>
          <Modal.Title>{ehEdicao ? 'Editar Prestador PJ' : 'Novo Prestador PJ'}</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="hpj-form-section">
          <div className="hpj-form-section-title">Dados do prestador</div>
          <Row>
            <Col md={5}>
              <Form.Group className={erros.codigoUsuario ? 'has-error' : ''}>
                <RequiredLabel>Usuário</RequiredLabel>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  isSearchable
                  noOptionsMessage={() => 'Nenhum usuário encontrado'}
                  onChange={alterarUsuario}
                  options={usuarioOptions}
                  placeholder="Código - Nome completo"
                  value={usuarioSelecionado}
                />
                <FieldError name="codigoUsuario" />
              </Form.Group>
            </Col>
            <Col md={7}>
              <Form.Group className={erros.razaoSocial ? 'has-error' : ''}>
                <RequiredLabel>Razão social</RequiredLabel>
                <Form.Control name="razaoSocial" value={form.razaoSocial} onChange={alterar} maxLength={150} />
                <FieldError name="razaoSocial" />
              </Form.Group>
            </Col>
            <Col md={4}><Form.Group><Form.Label>Nome fantasia</Form.Label><Form.Control name="nomeFantasia" value={form.nomeFantasia} onChange={alterar} maxLength={150} /></Form.Group></Col>
            <Col md={3}>
              <Form.Group className={erros.cnpj ? 'has-error' : ''}>
                <RequiredLabel>CNPJ</RequiredLabel>
                <MaskedInput
                  className="form-control"
                  guide={false}
                  inputMode="numeric"
                  mask={MASK_CNPJ_ALFANUMERICO}
                  name="cnpj"
                  onChange={alterar}
                  placeholder="00.000.000/0000-00"
                  showMask={false}
                  value={form.cnpj}
                />
                <FieldError name="cnpj" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={erros.dataAbertura ? 'has-error' : ''}>
                <RequiredLabel>Data abertura</RequiredLabel>
                <Form.Control name="dataAbertura" type="date" value={form.dataAbertura} onChange={alterar} />
                <FieldError name="dataAbertura" />
              </Form.Group>
            </Col>
          </Row>

          {/* DW-0982 P1: a remuneração NÃO é coletada no cadastro do PJ — vem do contrato.
              Em modo edição, exibimos a vigência atual (modalidade + valores + percentuais)
              apenas para leitura; alterações ocorrem no modal de Contrato ou na aba
              Histórico Valor-Hora. Em modo criação, nada de remuneração é exibido. */}
          {ehEdicao && (
            <>
              <Row>
                <Col xs={12}>
                  <div className="hpj-form-subtitle">Remuneração atual (somente leitura)</div>
                  <div className="hpj-aviso-inline">
                    A remuneração e os percentuais são definidos pelo contrato. Para alterar, use o cadastro do contrato.
                  </div>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Modalidade</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO ? 'Valor Fixo' : 'Por Hora'}
                      disabled
                      style={{ background: '#f5f5f5' }}
                    />
                  </Form.Group>
                </Col>
                {form.tipoRemuneracao === TIPO_REMUNERACAO_FIXO ? (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Valor fixo (R$)</Form.Label>
                        <Form.Control type="number" value={form.valorFixo} disabled style={{ background: '#f5f5f5' }} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Horas base / mês</Form.Label>
                        <Form.Control type="number" value={form.horasBase} disabled style={{ background: '#f5f5f5' }} />
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Valor hora (R$)</Form.Label>
                      <Form.Control type="number" value={form.valorHora} disabled style={{ background: '#f5f5f5' }} />
                    </Form.Group>
                  </Col>
                )}
              </Row>
              <Row>
                <Col xs={12}>
                  <div className="hpj-form-subtitle">Percentuais de retenção sobre o Valor NF</div>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>13ª NF (%)</Form.Label>
                    <Form.Control type="number" value={form.percentual13} disabled style={{ background: '#f5f5f5' }} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>14ª NF (%)</Form.Label>
                    <Form.Control type="number" value={form.percentual14} disabled style={{ background: '#f5f5f5' }} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Fundo (%)</Form.Label>
                    <Form.Control type="number" value={form.percentualFundo} disabled style={{ background: '#f5f5f5' }} />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </div>

        <div className="hpj-form-section">
          <div className="hpj-form-section-title">Dados bancários</div>
          <Row>
            <Col md={6}>
              <Form.Group className={erros.banco ? 'has-error' : ''}>
                <Form.Label>Banco</Form.Label>
                <Select
                  classNamePrefix="hpj-user-select"
                  filterOption={(option, input) => (
                    option.data.searchText
                      .toLowerCase()
                      .includes((input || '').toLowerCase())
                  )}
                  isClearable
                  isSearchable
                  noOptionsMessage={() => 'Nenhum banco encontrado'}
                  onChange={alterarBanco}
                  options={bancoOptions}
                  placeholder="Selecione o banco"
                  value={bancoSelecionado}
                />
                <FieldError name="banco" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={erros.agencia ? 'has-error' : ''}>
                <Form.Label>Agência</Form.Label>
                <Form.Control inputMode="numeric" name="agencia" value={form.agencia} onChange={alterar} maxLength={5} />
                <FieldError name="agencia" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={erros.conta ? 'has-error' : ''}>
                <Form.Label>Conta</Form.Label>
                <Form.Control inputMode="numeric" name="conta" value={form.conta} onChange={alterar} maxLength={20} />
                <FieldError name="conta" />
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="hpj-form-section">
          <div className="hpj-form-section-title">
            Endereço
            {buscandoCep && (
              <span className="ml-2 text-muted" style={{ fontSize: '0.85rem' }}>
                <Spinner animation="border" size="sm" className="mr-1" />
                Buscando endereço…
              </span>
            )}
          </div>
          <Row>
            <Col md={3}>
              <Form.Group className={erros.enderecoCep ? 'has-error' : ''}>
                <RequiredLabel>CEP</RequiredLabel>
                <InputGroup>
                  <MaskedInput
                    className="form-control"
                    guide={false}
                    inputMode="numeric"
                    mask={MASK_CEP}
                    name="enderecoCep"
                    onChange={alterar}
                    placeholder="00000-000"
                    showMask={false}
                    value={form.enderecoCep}
                    disabled={buscandoCep}
                  />
                  <InputGroup.Append>
                    <Button
                      type="button"
                      variant="primary"
                      className="hpj-cep-btn"
                      onClick={buscarEnderecoCep}
                      disabled={buscandoCep}
                    >
                      OK
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
                <FieldError name="enderecoCep" />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className={erros.enderecoUf ? 'has-error' : ''}>
                <RequiredLabel>UF</RequiredLabel>
                <Form.Control as="select" name="enderecoUf" value={form.enderecoUf} onChange={alterarUf} disabled={buscandoCep}>
                  <option value="">Selecione</option>
                  {ufs.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </Form.Control>
                <FieldError name="enderecoUf" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={erros.enderecoCidade ? 'has-error' : ''}>
                <RequiredLabel>Cidade</RequiredLabel>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  isSearchable
                  isDisabled={!form.enderecoUf || buscandoCep}
                  noOptionsMessage={() => 'Nenhuma cidade encontrada'}
                  onChange={alterarCidade}
                  options={cidadeOptions}
                  placeholder={form.enderecoUf ? 'Selecione a cidade' : 'Selecione a UF primeiro'}
                  value={cidadeSelecionada}
                />
                <FieldError name="enderecoCidade" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={erros.enderecoBairro ? 'has-error' : ''}>
                <RequiredLabel>Bairro</RequiredLabel>
                <Form.Control name="enderecoBairro" value={form.enderecoBairro} onChange={alterar} maxLength={60} disabled={buscandoCep} />
                <FieldError name="enderecoBairro" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className={erros.enderecoLogradouro ? 'has-error' : ''}>
                <RequiredLabel>Logradouro</RequiredLabel>
                <Form.Control name="enderecoLogradouro" value={form.enderecoLogradouro} onChange={alterar} maxLength={100} disabled={buscandoCep} />
                <FieldError name="enderecoLogradouro" />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className={erros.enderecoNumero ? 'has-error' : ''}>
                <RequiredLabel>Número</RequiredLabel>
                <Form.Control inputMode="numeric" name="enderecoNumero" value={form.enderecoNumero} onChange={alterar} maxLength={4} disabled={buscandoCep} />
                <FieldError name="enderecoNumero" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className={erros.enderecoComplemento ? 'has-error' : ''}>
                <Form.Label>Complemento</Form.Label>
                <Form.Control name="enderecoComplemento" value={form.enderecoComplemento} onChange={alterar} maxLength={60} disabled={buscandoCep} />
                <FieldError name="enderecoComplemento" />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {!ehEdicao && (
        <div className="hpj-form-section hpj-form-section--last">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <div className="hpj-form-section-title mb-0">Sócios</div>
              <div className="hpj-modal-helper">Soma obrigatória: 100%</div>
            </div>
            <Button className="hpj-btn" size="sm" variant="outline-secondary" onClick={() => setModalSocio(true)}>Adicionar sócio</Button>
          </div>
          <div className="hpj-card hpj-card--overflow hpj-socios-card">
            <Table className="hpj-table hpj-table-compact" responsive>
              <thead><tr><th>Nome</th><th>CPF</th><th>%</th><th /></tr></thead>
              <tbody>
                {socios.map(socio => (
                  <tr key={socio.cpf}>
                    <td>{socio.nome}</td>
                    <td>{socio.cpf}</td>
                    <td>{Number(socio.percentualParticipacao).toFixed(2)}</td>
                    <td className="text-right"><Button className="hpj-btn hpj-btn-danger" size="sm" variant="outline-secondary" onClick={() => removerSocio(socio.cpf)}>Remover</Button></td>
                  </tr>
                ))}
                {!socios.length && <tr><td colSpan="4" className="text-muted text-center">Nenhum sócio informado</td></tr>}
              </tbody>
            </Table>
          </div>
        </div>
        )}
        <ModalAddSocio show={modalSocio} onHide={() => setModalSocio(false)} onAdicionar={adicionarSocio} />
      </Modal.Body>
      <Modal.Footer>
        <Button className="hpj-btn" variant="outline-secondary" onClick={onHide}>Cancelar</Button>
        <Button className="hpj-btn hpj-btn-primary" variant="primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : ehEdicao ? 'Atualizar' : 'Salvar'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddPJ;
